import type {
  RemoveBgApiResponse,
  RemoveBgErrorResponse,
  RemoveBgHistoryItem,
  RemoveBgJob
} from "@/types/removebg";

interface UploadRemoveBgOptions {
  background?: string;
  returnMask?: boolean;
  signal?: AbortSignal;
}

function ensureOkStatus(response: Response) {
  if (response.ok) return;
  throw new Error(`Falha na solicitação: ${response.status}`);
}

export async function uploadRemoveBgImage(file: File, options: UploadRemoveBgOptions = {}): Promise<RemoveBgApiResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (options.background) formData.append("background", options.background);
  if (typeof options.returnMask === "boolean") {
    formData.append("return_mask", String(options.returnMask));
  }

  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest"
  };

  if (process.env.NEXT_PUBLIC_REMOVE_BG_FRONTEND_KEY) {
    headers["X-Frontend-Key"] = process.env.NEXT_PUBLIC_REMOVE_BG_FRONTEND_KEY;
  }

  const response = await fetch("/api/removebg", {
    method: "POST",
    body: formData,
    headers,
    signal: options.signal
  });

  const data = (await response.json().catch(() => null)) as RemoveBgApiResponse | RemoveBgErrorResponse | null;

  if (!response.ok) {
    if (data && "message" in data) {
      throw new Error(data.message);
    }

    throw new Error(`Erro ao processar imagem (status ${response.status}).`);
  }

  if (!data) {
    throw new Error("Resposta inesperada do servidor.");
  }

  return data;
}

export async function fetchRemoveBgHistory(limit = 20): Promise<RemoveBgHistoryItem[]> {
  const response = await fetch(`/api/removebg/history?limit=${limit}`, {
    method: "GET",
    headers: {
      "Accept": "application/json"
    }
  });

  if (!response.ok) {
    throw new Error("Não foi possível carregar o histórico.");
  }

  const payload = (await response.json()) as {
    status: string;
    items?: RemoveBgHistoryItem[];
  };

  return payload.items ?? [];
}

export async function downloadRemoveBgVariant(jobId: string, variant: "preview" | "hd"): Promise<Blob> {
  const url = `/api/removebg/${jobId}/download?variant=${variant}`;
  const response = await fetch(url);
  ensureOkStatus(response);
  return await response.blob();
}

export function isRemoveBgJob(response: RemoveBgApiResponse): response is RemoveBgJob {
  return response.status === "success";
}
